import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';

import OnboardingItemFabrica from '../OnboardingItemFabrica/OnboardingItemFabrica';
import { Button, Form, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Delete } from '../../../assets/delete-black-icon.svg';
import { ReactComponent as Pluse } from '../../../assets/pluse-black-icon.svg';
import { ReactComponent as Save } from '../../../assets/save-disket-icon.svg';
import { ReactComponent as Cancel } from '../../../assets/cancel-icon-min.svg';
import CustomTable from '../../../components/CustomTable/CustomTable';
import CustomSortIcon from '../../../components/CustomSortIcon/CustomSortIcon';
import { textSort } from '../../../helpers/textSort';
import { useDispatch, useSelector } from 'react-redux';

import {
  createCompositeInputElement,
  deleteCompositeInputElement,
  editCompositeInputElement,
  getOnboardingById,
} from '../../../data/store/onboarding/onboardingActions';

import { getValueObj } from '../OnboardingCategory';

export const EditableContext = React.createContext(null);

export const OnboardingTableContext = React.createContext(null);

const EditableRow = ({ record, ...props }) => {
  const ctx = useContext(OnboardingTableContext);
  const compositeId = ctx?.compositeId;
  const itemsSchema = ctx?.itemsSchema ?? [];

  const [form] = Form.useForm();

  useEffect(() => {
    if (!record || !compositeId) return;
    const nested = {};
    itemsSchema.forEach((item) => {
      const el = item.form_input_element;
      if (!el?.id) return;
      const raw = record[el.id];
      if (raw === undefined || raw === null) return;
      if (el.type === 'DATE' || el.type === 'DATE_TIME') {
        const d = dayjs(raw);
        nested[el.id] = d.isValid() ? d : undefined;
      } else {
        nested[el.id] = raw;
      }
    });
    form.resetFields();
    form.setFieldsValue({ [compositeId]: nested });
  }, [record, compositeId, itemsSchema]);

  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const focusFirstControl = (root) => {
  if (!root) return;
  const focusable = root.querySelector(
    'input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]), textarea, .ant-select-selector, .ant-picker-input input',
  );
  if (!focusable) return;
  focusable.focus?.();
  if (focusable.matches?.('.ant-select-selector')) {
    focusable.click?.();
  }
};

const EditableCell = ({
  composite,
  editing,
  children,
  dataIndex,
  formItem,
  onboardingId,
  focusFirstInRow,
  onFocusConsumed,
  ...restProps
}) => {
  const form = useContext(EditableContext);
  const fabricaWrapRef = useRef(null);

  useLayoutEffect(() => {
    if (!focusFirstInRow) return;
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      setTimeout(() => {
        if (cancelled) return;
        focusFirstControl(fabricaWrapRef.current);
        onFocusConsumed?.();
      }, 0);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [focusFirstInRow, onFocusConsumed]);

  const getInputItem = () => {
    if (!dataIndex || dataIndex === 'operation') return null;
    return formItem?.form_composite_input_element?.form_composite_input_element_items?.find(
      (i) => i.form_input_element?.id === dataIndex,
    );
  };

  if (dataIndex === 'operation') {
    return <td {...restProps}>{children}</td>;
  }

  if (editing) {
    const inputItem = getInputItem();
    if (!inputItem) {
      return <td {...restProps}>{children}</td>;
    }
    return (
      <td {...restProps}>
        <div ref={fabricaWrapRef} className="onboarding-table-fabrica-cell">
          <OnboardingItemFabrica
            form={form}
            formItem={inputItem}
            onboardingId={onboardingId}
            composite={composite}
            deferRemoteSubmit
          />
        </div>
      </td>
    );
  }

  return <td {...restProps}>{children}</td>;
};

const OnboardingTableFabrica = ({ formItem }) => {
  const { t } = useTranslation();
  const { onboardingById } = useSelector((state) => state.onboardingReducer);
  const dispatch = useDispatch();

  const compositeElementId = formItem?.form_composite_input_element?.id;
  const onboardingId = onboardingById?.id;

  const tableData = useMemo(() => {
    const stored = onboardingById?.form_composite_input_element_submissions?.filter(
      (i) => i.form_composite_input_element_id === compositeElementId,
    );
    if (!stored?.length) return [];
    const rows = [];
    stored.forEach((el) => {
      const parsingData = { id: el.id, key: el.id };
      el?.form_input_element_submissions?.forEach((i) => {
        parsingData[i.form_input_element_id] = i?.value_text ?? i?.value_number ?? i?.value_boolean;
      });
      rows.push(parsingData);
    });
    return rows;
  }, [onboardingById?.form_composite_input_element_submissions, compositeElementId]);

  const data = formItem?.form_composite_input_element;

  const itemsSchema = useMemo(
    () =>
      data?.form_composite_input_element_items
        ?.slice()
        ?.sort((a, b) => a?.element_order - b?.element_order),
    [data?.form_composite_input_element_items],
  );

  const [editingKey, setEditingKey] = useState('');
  const [draftRows, setDraftRows] = useState([]);
  const [savingKey, setSavingKey] = useState('');
  const [focusRowKey, setFocusRowKey] = useState('');

  const firstFieldId = useMemo(() => itemsSchema?.[0]?.form_input_element?.id, [itemsSchema]);

  const consumeRowFocus = useCallback(() => {
    setFocusRowKey('');
  }, []);

  const mergedTableData = useMemo(() => [...tableData, ...draftRows], [tableData, draftRows]);

  const handleSubmitComposite = useCallback(
    async (rowRecord, rowForm) => {
      if (!rowForm || !compositeElementId || !onboardingId || !itemsSchema) return;

      setSavingKey(rowRecord.key);
      try {
        await rowForm.validateFields();

        const values = rowForm.getFieldsValue();
        const compositeValues = values?.[compositeElementId] ?? {};

        const obj = {
          form_composite_input_element_id: compositeElementId,
          form_submission_id: onboardingId,
          form_input_element_submissions: [],
        };

        itemsSchema.forEach((schemaItem) => {
          const inputEl = schemaItem?.form_input_element;
          const fieldId = inputEl?.id;
          if (!fieldId) return;

          const fieldValue = compositeValues?.[fieldId];

          if (
            fieldValue === undefined ||
            fieldValue === null ||
            fieldValue === ''
          ) {
            return;
          }

          obj.form_input_element_submissions.push({
            form_input_element_id: fieldId,
            form_submission_id: onboardingId,
            ...getValueObj(inputEl, fieldValue),
          });
        });

        if (rowRecord?.id) {
          dispatch(editCompositeInputElement({ id: rowRecord.id, ...obj }));
        } else {
          dispatch(createCompositeInputElement(obj));
          setDraftRows((prev) => prev.filter((r) => r.key !== rowRecord.key));
        }

        setEditingKey('');
        // dispatch(getOnboardingById(onboardingId));
      } catch (e) {
        console.log('Row validation failed:', e);
      } finally {
        setSavingKey('');
      }
    },
    [
      compositeElementId,
      onboardingId,
      itemsSchema,
      dispatch,
      setDraftRows,
      setEditingKey,
    ],
  );

  const handleDelete = useCallback(
    (rowRecord) => {
      if (!rowRecord) return;

      if (rowRecord?.id) {
        dispatch(deleteCompositeInputElement(rowRecord.id));
        onboardingId && dispatch(getOnboardingById(onboardingId));
      } else {
        setDraftRows((prev) => prev.filter((r) => r.key !== rowRecord.key));
      }

      setEditingKey((k) => (k === rowRecord.key ? '' : k));
    },
    [dispatch, onboardingId],
  );

  const tableContextValue = useMemo(
    () => ({
      compositeId: data?.id,
      itemsSchema,
    }),
    [data?.id, itemsSchema],
  );

  const columns = useMemo(() => {
    const table = itemsSchema?.map((item) => {
      const { id, description, submit_value_Type } = item.form_input_element;
      return {
        title: description,
        dataIndex: id,
        key: item?.id,
        editable: true,
        sorter: (a, b) =>
          submit_value_Type === 'VALUE_TEXT'
            ? textSort(String(a[id] ?? ''), String(b[id] ?? ''))
            : (Number(a[id]) || 0) - (Number(b[id]) || 0),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      };
    });
    if (!table) return [];
    table.push({
      title: '',
      className: 'composit-table-action',
      dataIndex: 'operation',
      align: 'center',
      editable: true,
      render: (_, record) => (
        <>
          {editingKey === record.key && (
            <SaveRowActionButton rowRecord={record} />
          )}
          {editingKey === record.key && (
            <Button
              icon={<Cancel />}
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                setEditingKey('');
              }}
            />
          )}
          {editingKey !== record.key && (
            <Button
              icon={<Delete />}
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(record);
              }}
            />
          )}
        </>
      ),
    });
    return table;
  }, [itemsSchema, editingKey, savingKey, handleSubmitComposite, handleDelete]);

  function SaveRowActionButton({ rowRecord }) {
    const rowForm = useContext(EditableContext);

    return (
      <Button
        icon={<Save />}
        type="text"
        disabled={savingKey === rowRecord.key}
        onClick={(e) => {
          e.stopPropagation();
          handleSubmitComposite(rowRecord, rowForm);
        }}
      />
    );
  }

  SaveRowActionButton.propTypes = {
    rowRecord: PropTypes.object.isRequired,
  };

  const mappedColumns = useMemo(
    () =>
      columns?.map((col) => ({
        ...col,
        onCell: (record) => ({
          composite: formItem?.form_composite_input_element?.id,
          editing: record.key === editingKey,
          dataIndex: col.dataIndex,
          formItem,
          onboardingId: onboardingById?.id,
          focusFirstInRow:
            Boolean(firstFieldId) &&
            focusRowKey === record.key &&
            record.key === editingKey &&
            col.dataIndex === firstFieldId,
          onFocusConsumed: consumeRowFocus,
        }),
      })),
    [
      columns,
      formItem?.form_composite_input_element?.id,
      onboardingById?.id,
      firstFieldId,
      focusRowKey,
      editingKey,
      consumeRowFocus,
    ],
  );

  const components = useMemo(
    () => ({
      body: {
        row: EditableRow,
        cell: EditableCell,
      },
    }),
    [],
  );

  const handleAdd = useCallback(() => {
    const newKey = uuidv4();
    setDraftRows((prev) => [...prev, { key: newKey, id: undefined }]);
    setEditingKey(newKey);
    setFocusRowKey(newKey);
  }, []);

  return (
    <OnboardingTableContext.Provider value={tableContextValue}>
      <div className="client portfolio-table  instrument-table-wrapper">
        <p className="onboarding-table-title">{formItem?.form_composite_input_element?.description}</p>
        <CustomTable
          className="onboarding-table-table"
          components={components}
          rowHoverable={false}
          dataSource={mergedTableData}
          columns={mappedColumns}
          onRow={(record) => ({
            record,
            onClick: () => setEditingKey(record.key),
          })}
          pagination={false}
        />
        <Row className="add-btn-wrapper" justify={'end'}>
          <Button
            type="text"
            className="holdings-add-btn add-instrument"
            onClick={handleAdd}
            icon={<Pluse />}
            iconPosition={'end'}
          >
            {t('ADD')}
          </Button>
        </Row>
      </div>
    </OnboardingTableContext.Provider>
  );
};

export default React.memo(OnboardingTableFabrica);

EditableRow.propTypes = {
  record: PropTypes.object,
};

OnboardingTableFabrica.propTypes = {
  formItem: PropTypes.object.isRequired,
};

EditableCell.propTypes = {
  composite: PropTypes.string,
  editing: PropTypes.bool,
  children: PropTypes.node,
  dataIndex: PropTypes.string,
  formItem: PropTypes.object,
  onboardingId: PropTypes.string,
  focusFirstInRow: PropTypes.bool,
  onFocusConsumed: PropTypes.func,
};
